import { apiGet, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseEarningGpt {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private lastFetchedAt: Date | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' },
      { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' },
      { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' },
      { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' },
      { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' },
      { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;

    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    this.attachStyles();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    this.config.onReady?.();
    cancelSelectEvent(this.shadowRoot);
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title stock">${this.getTitleIconSvg()} Earning</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<tooltip-wrapper id="lastUpdatedTooltip"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.625 6.625C0.625 7.41293 0.780195 8.19315 1.08172 8.9211C1.38325 9.64905 1.82521 10.3105 2.38236 10.8676C2.93951 11.4248 3.60095 11.8667 4.3289 12.1683C5.05685 12.4698 5.83707 12.625 6.625 12.625C7.41293 12.625 8.19315 12.4698 8.9211 12.1683C9.64905 11.8667 10.3105 11.4248 10.8676 10.8676C11.4248 10.3105 11.8667 9.64905 12.1683 8.9211C12.4698 8.19315 12.625 7.41293 12.625 6.625C12.625 5.0337 11.9929 3.50758 10.8676 2.38236C9.74242 1.25714 8.2163 0.625 6.625 0.625C5.0337 0.625 3.50758 1.25714 2.38236 2.38236C1.25714 3.50758 0.625 5.0337 0.625 6.625Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.625 3.2915V6.62484L8.625 8.62484" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> </svg><tooltip><span>Last Updated</span><strong>N/A</strong></tooltip></tooltip-wrapper><i class="pi pi-ellipsis-v"></i></div>
        </div>

        <div class="inBody">
        <div class="optionsRow">
                ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                <div class="trader-sm-card" id="selected-symbol">
                ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: "stock", name: "" })}
                </div>
                <span id="symbol-dropdown-placeholder"></span>
                </div>`}
                <span></span>
            </div>
          <div id="marketCapSummary" data-testing-id="market-cap-summary"></div>
          <div id="cryptoContainer" data-testing-id="crypto-container"></div>
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
  }

  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async setupChart(symbol: string): Promise<void> {
    try {
      const apiSymbol = symbol.includes('_') ? symbol.split('_')[0] : symbol;
      this.config.symbol = apiSymbol;

      const res = await apiGet({
        method: 'GET',
        url: `${APIURL}/v1/company/earning?symbol=${encodeURIComponent(apiSymbol)}`,
        headers: {
          'Content-Type': 'application/json'
        }
      });
  
      if (!res.ok) throw new Error("Failed to fetch earnings data");
  
      const json = await res.json();
  
      this.currentData = json; // store full response
      this.lastFetchedAt = new Date();
  
      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
  
    } catch (err) {
      console.error("Error fetching earnings data:", err);
    }
  }

  private trendingCoins(): void {
    if (!this.currentData?.topIndex?.data) return;
    this.renderTrendingCoins(this.currentData.topIndex.data);
  }

  private renderTrendingCoins(cryptoData: any): void {
    const container = this.shadowRoot?.querySelector('#cryptoContainer');
    if (!container) return;

    if (!cryptoData) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const currentIndexValue = cryptoData.currentIndexValue ?? 0;
    const percentChange = cryptoData.percentChange ?? 0;
    const changeClass = percentChange >= 0 ? 'positive' : 'negative';
    const changeSymbol = percentChange >= 0 ? '+' : '';

    container.innerHTML = `
      <div class="cryptoCard" data-testing-id="top-100-index-card">
        <h2 data-testing-id="top-100-index-title">Top 100 Coin Index</h2>
        <div class="chartFig">
          <div class="valueWrapper" data-testing-id="index-value-wrapper">
            <p data-testing-id="index-value">$${currentIndexValue.toFixed(2)}T</p>
            <span class="expand"></span>
            <span class="${changeClass}" data-testing-id="index-change" data-change="${percentChange}">${changeSymbol}${percentChange.toFixed(2)}% (24h)</span>
          </div>
          <div class="inChart"><div id="cryptoMarketChart" data-testing-id="crypto-market-chart"></div></div>
        </div>
      </div>
    `;

    this.drawChart();
  }

  private drawChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#cryptoMarketChart') as HTMLElement;
    if (!container || !this.currentData?.topIndex?.data?.chartData) return;

    const chartData = this.currentData.topIndex?.data.chartData;
    const seriesData = chartData.map((item: any) => [item.timestamp, item.value]);

    const instance = echarts.getInstanceByDom(container);
    if (instance) echarts.dispose(instance);

    const chart = echarts.init(container);
    const option = {
      backgroundColor: 'transparent',
      grid: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        show: false
      },
      yAxis: {
        type: 'value',
        show: false
      },
      series: [{
        data: seriesData.map((item: any) => item[1]),
        type: 'line',
        smooth: true,
        symbol: 'none',
        lineStyle: {
          width: 1.5,
          color: '#00E396'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(0, 227, 150, 0.25)' },
            { offset: 1, color: 'rgba(0, 227, 150, 0)' }
          ])
        }
      }]
    };

    chart.setOption(option);
    
    // Add data-testing-id to chart after render
    setTimeout(() => {
      const chartSvg = this.shadowRoot.querySelector('#cryptoMarketChart svg');
      if (chartSvg) {
        chartSvg.setAttribute('data-testing-id', 'crypto-market-chart-svg');
      }
    }, 500);
  }

  private pickEarningsValue(source: Record<string, any>, keys: string[]): any {
    for (const key of keys) {
      const value = source[key];
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return null;
  }

  private formatEps(value: number | null | undefined): string {
    if (value == null) return '-';
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    return `$${num.toFixed(2)}`;
  }

  private formatUsDate(dateStr?: string | null): string {
    if (!dateStr) return '-';
    const normalized = String(dateStr).trim();
    if (!normalized || normalized === '-') return '-';
    const d = new Date(normalized.includes('T') ? normalized : `${normalized}T12:00:00`);
    if (Number.isNaN(d.getTime())) return normalized;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  private formatSurprisePercent(value: number | null | undefined): string {
    if (value == null) return '-';
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    return `${num.toFixed(2)}%`;
  }

  private formatRevenue(value: number | null | undefined): string {
    if (value == null) return '-';
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    const abs = Math.abs(num);
    const sign = num < 0 ? '-' : '';
    if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(3)}T`;
    if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(3)}B`;
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(3)}M`;
    return `${sign}$${abs.toLocaleString()}`;
  }

  private renderEarnings(): void {
    const container = this.shadowRoot.querySelector('#cryptoContainer');
    if (!container) return;

    const eps = this.currentData?.data?.REPORTED_EPS || {};
    const revenue = this.currentData?.data?.REPORTED_REVENUE || {};

    const reportedEPS = this.formatEps(eps.REPORTED_EPS);
    const consensusEPS = this.formatEps(eps.Consensus_EPS_Forecast);
    const epsSurpriseRaw = eps.EPS_Surprise ?? 0;
    const epsSurprise = this.formatSurprisePercent(epsSurpriseRaw);
    const nextEarningsDate = this.formatUsDate(
      this.pickEarningsValue(eps, [
        'Next_Earnings_Report_Date',
        'Next_Earnings_Date',
        'next_earnings_date',
        'Last_Earnings_Announcement'
      ])
    );
    const totalRevenue = this.formatRevenue(revenue.Total_Revenue);

    const surpriseClass = Number(epsSurpriseRaw) >= 0 ? 'positive' : 'negative';
    const rowDivider = 'border-bottom:1px solid rgba(255,255,255,0.08);padding-bottom:12px;margin-bottom:12px;';
    const doubleDivider = 'border-bottom:3px double rgba(255,255,255,0.12);padding-bottom:12px;margin-bottom:12px;';

    container.innerHTML = `
      <div class="chartDv fearAndGreedMain" data-testing-id="earnings-card">
        <div class="cryptoCard">

          <div class="valueWrapper" style="${rowDivider}">
            <p>Reported EPS</p> 
            <span class="expand"></span>
            <span style="font-weight:600;color:var(--text-white);">${reportedEPS}</span>
          </div>

          <div class="chartFig" style="display:block;${rowDivider}">
            <div class="valueWrapper">
              <p>Next Earnings Report Date</p>
              <h3 data-testing-id="next-earnings-date">${nextEarningsDate}</h3>
            </div>
          </div>

          <div class="valueWrapper" style="margin-bottom:8px;">
            <p>Consensus EPS Forecast</p>
            <span class="expand"></span>
            <span style="font-weight:600;color:var(--text-white);">${consensusEPS}</span>
          </div>

          <div class="valueWrapper" style="${doubleDivider}">
            <p>EPS Surprise</p>
            <span class="expand"></span>
            <span class="${surpriseClass}" style="font-weight:600;">${epsSurprise}</span>
          </div>

          <div class="detailItem noBg" data-testing-id="total-revenue">
            <p class="title">Total Revenue</p>
            <p class="para">${totalRevenue}</p>
          </div>
        </div>
      </div>
    `;
  }

  private formatLastUpdated(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    };
    return date.toLocaleString('en-US', options).replace(',', '').replace(/\//g, '-') + ' EST';
  }

  private updateLastUpdatedTooltip(): void {
    const lastUpdated = this.lastFetchedAt ? this.formatLastUpdated(this.lastFetchedAt) : 'N/A';
    const tooltip = this.shadowRoot.querySelector('#lastUpdatedTooltip tooltip strong');
    if (tooltip) tooltip.innerHTML = lastUpdated.replace(' ', '<br>');
  }

  private updateDisplay(): void {
    this.updateLastUpdatedTooltip();
  
    if (!this.currentData?.data) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
  
    this.renderEarnings();
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseEarningGpt: typeof TraderverseEarningGpt; } }
if (typeof window !== 'undefined') { window.TraderverseEarningGpt = TraderverseEarningGpt; }